import React from "react";
import { Box, Card, CardContent, Typography, Stack, Chip } from "@mui/material";
import { motion } from "framer-motion";
import {
  PieChart,
  Pie,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from "recharts";
import ClassRoundedIcon from "@mui/icons-material/ClassRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";

const MotionCard = motion(Card);

const glassMixin = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: "rgba(255,255,255,0.65)",
  border: "1px solid rgba(255,255,255,0.85)",
  boxShadow:
    "0 8px 32px rgba(99,102,241,0.08), 0 1px 0 rgba(255,255,255,0.9) inset",
};

const PIE_COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const val = payload[0].value;
    const grade =
      val >= 90
        ? "Excellent"
        : val >= 75
        ? "Good"
        : val >= 60
        ? "Average"
        : "Needs Work";
    const gradeColor =
      val >= 90
        ? "#10b981"
        : val >= 75
        ? "#6366f1"
        : val >= 60
        ? "#f59e0b"
        : "#ef4444";
    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
          border: "1px solid rgba(99,102,241,0.3)",
          borderRadius: "14px",
          px: 2,
          py: 1.5,
          minWidth: 150,
          boxShadow: "0 16px 40px rgba(0,0,0,0.3)",
        }}
      >
        <Typography
          sx={{
            color: "#a5b4fc",
            fontSize: "0.68rem",
            fontWeight: 700,
            mb: 0.5,
            letterSpacing: "0.06em",
          }}
        >
          {payload[0].name}
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontSize: "1.35rem",
            fontWeight: 800,
            fontFamily: "'Syne', sans-serif",
            lineHeight: 1,
          }}
        >
          {val?.toFixed(1)}
          <Typography
            component="span"
            sx={{ color: "#818cf8", fontSize: "0.8rem", ml: 0.4 }}
          >
            / 100
          </Typography>
        </Typography>
        <Typography
          sx={{
            color: gradeColor,
            fontSize: "0.7rem",
            fontWeight: 700,
            mt: 0.5,
          }}
        >
          ● {grade}
        </Typography>
      </Box>
    );
  }
  return null;
};

const ClassChart = ({ data }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Card sx={{ mb: 3, borderRadius: "24px", ...glassMixin }}>
        <CardContent
          sx={{
            p: "24px !important",
            textAlign: "center",
            py: "52px !important",
          }}
        >
          <ClassRoundedIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1.5 }} />
          <Typography
            variant="h6"
            sx={{
              color: "#9ca3af",
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
            }}
          >
            No Class Data
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
            No class performance data available.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const formatted = data
    .map((item) => ({
      name: item.inststudent__instclass__name || "Unknown",
      value: parseFloat((item.avg || 0).toFixed(1)),
    }))
    .sort((a, b) => b.value - a.value);

  const topClass = formatted[0];

  return (
    <MotionCard
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
      sx={{
        mb: 3,
        borderRadius: "24px",
        overflow: "hidden",
        position: "relative",
        ...glassMixin,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: "linear-gradient(90deg, #8b5cf6 0%, #6366f1 100%)",
        }}
      />

      <CardContent sx={{ p: "24px !important" }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          mb={3}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(99,102,241,0.15) 100%)",
                  border: "1px solid rgba(139,92,246,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ClassRoundedIcon sx={{ color: "#8b5cf6", fontSize: 20 }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 700,
                  color: "#111827",
                  fontSize: "1rem",
                }}
              >
                Class Performance
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{ color: "#9ca3af", ml: "54px", fontSize: "0.78rem" }}
            >
              Average scores by class
            </Typography>
          </Box>

          {topClass && (
            <Chip
              icon={<EmojiEventsRoundedIcon style={{ fontSize: 14 }} />}
              label={`Top: ${topClass.name}`}
              size="small"
              sx={{
                background: "rgba(245,158,11,0.1)",
                color: "#f59e0b",
                fontWeight: 700,
                fontSize: "0.72rem",
                border: "1px solid rgba(245,158,11,0.25)",
                borderRadius: "10px",
                "& .MuiChip-icon": { color: "inherit", fontSize: 14 },
              }}
            />
          )}
        </Stack>

        <ResponsiveContainer width="100%" height={320}>
          <PieChart>
            <Pie
              data={formatted}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={120}
              innerRadius={60}
              paddingAngle={4}
              label={({ name, value }) => `${name}: ${value}%`}
              labelLine={false}
            >
              {formatted.map((_, index) => (
                <Cell
                  key={index}
                  fill={PIE_COLORS[index % PIE_COLORS.length]}
                  stroke="rgba(255,255,255,0.8)"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              align="center"
              iconType="circle"
              wrapperStyle={{
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "#4b5563",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </MotionCard>
  );
};

export default ClassChart;
